import { FormattedNumber, useIntl } from 'react-intl';
import { useEffect, useMemo, useState } from 'react';
import { LoadingView } from '../../../components';
import { Theme, useStyletron } from 'baseui';
import { Block } from 'baseui/block';
import { ParagraphXSmall } from 'baseui/typography';
import { HeatMapGrid } from 'react-grid-heatmap'
import { groupBy } from '../../../utils/arrayUtils';
import { useGetHeatmapEnergy } from './useGetHeatmapEnergy';
import { MeterMedium, MeterType } from '../../../services/energyHeatmap';
import { EmptyState } from '../../../components/EmptyState';

interface IState {
    xLabels: string[],
    yLabels: string[],
    values: any[],
    fact: number
}
const EnergyHeatmap = (props: { showLegend?: boolean, start: Date, end: Date, typeFilters: MeterType[], mediumsFilters: MeterMedium[], objectId?: string, resourceGroupId?: string, dimension?: string, colors?: string[], valueFactor?: number }) => {
    const intl = useIntl();
    const [css, theme] = useStyletron();
    const [state, setState] = useState<IState>({ xLabels: [], yLabels: [], values: [], fact: 1 });
    const colors = useMemo(() => props.colors ?? getColors(theme), [theme]);
    const { data, maxValue: dataMaxValue, isLoading, error, isSuccess, anomalies } = useGetHeatmapEnergy(props.start, props.end, props.typeFilters, props.mediumsFilters, props.objectId, props.resourceGroupId);

    const maxValue = props.valueFactor ? dataMaxValue * props.valueFactor : dataMaxValue
    useEffect(() => {
        if (!data || data.length === 0) { setState({ xLabels: [], yLabels: [], values: [], fact: 1 }); return; }

        const groupedByMonth = groupBy(data, 'month').slice().sort((a, b) => new Date(a.key).getTime() - new Date(b.key).getTime());
        const xLabels = new Array(24).fill(0).map((_, i) => i % 4 ? '' : `${intl.formatTime(new Date(1900, 0, 0, i))}`);
        const yLabels = groupedByMonth.map(i => intl.formatDate(new Date(i.key), { month: 'short' }));
        let myData: number[][] = [];
        for (let i = 0; i < groupedByMonth.length; i++) {
            myData.push(groupedByMonth[i].values.map(v => props.valueFactor ? v.sumValue * props.valueFactor : v.sumValue));
        }
        const newState = { xLabels: xLabels, yLabels: yLabels, values: myData, fact: colors ? maxValue / (colors.length) : 1 };
        setState(newState);
    }, [data]);

    const dim = props.dimension ?? 'kW';

    if ((!data || data.length === 0) && isSuccess)
        return <EmptyState header={'No data'} ></EmptyState>
    return (
        <Block display={'flex'} flexDirection={'column'} width={'100%'} gridGap={theme.sizing.scale400}>
            <LoadingView isLoading={isLoading} isSucess={isSuccess} error={error}>
                {(props.showLegend === undefined || props.showLegend) &&
                    <Block display={'flex'} gridGap={theme.sizing.scale400} flexWrap>
                        {colors?.map((c, index) =>
                            <Block display={'inline-flex'} gridGap={theme.sizing.scale100} key={index}>
                                <div className={css({
                                    width: '10px',
                                    height: '20px',
                                    borderRadius: theme.sizing.scale100,
                                    background: c,
                                })}></div>
                                <ParagraphXSmall color={theme.colors.contentTertiary} margin={0}><FormattedNumber value={Math.round(index * state.fact)}></FormattedNumber>-<FormattedNumber value={Math.round((index + 1) * state.fact)}></FormattedNumber> {dim}</ParagraphXSmall>
                            </Block>)}

                    </Block>
                }
                <HeatMapGrid
                    data={state.values}
                    xLabels={state.xLabels}
                    yLabels={state.yLabels}
                    cellHeight="2rem"
                    xLabelsPos="bottom"
                    cellRender={(x, y, value) =>
                        <div title={`${value?.toFixed(1)} ${dim}`} className={css({ width: '100%', height: '100%' })}>{' '}</div>}
                    xLabelsStyle={index => ({
                        overflow: undefined,
                        color: theme.colors.contentTertiary,
                        fontSize: theme.typography.ParagraphXSmall.fontSize,
                        fontFamily: theme.typography.ParagraphXSmall.fontFamily
                    })}
                    yLabelsStyle={index => ({
                        color: theme.colors.contentTertiary,
                        fontSize: theme.typography.ParagraphXSmall.fontSize,
                        fontFamily: theme.typography.ParagraphXSmall.fontFamily
                    })}
                    cellStyle={(x, y, ratio) => ({
                        background: getColor(ratio),
                        border: `1px solid ${theme.colors.backgroundPrimary}`,
                        borderRadius: theme.sizing.scale100,
                    })}
                />

            </LoadingView>
        </Block>
    )

    function getColor(item: any) {
        return colors[Math.trunc(item * (colors.length - 1))];
    }
}

export default EnergyHeatmap;



const getColors = (theme: Theme) => {
    return [
        theme.colors.positive400,
        theme.colors.positive100,
        theme.colors.warning100,
        theme.colors.warning400,
        theme.colors.negative100,
        theme.colors.negative400,
    ]
}